import { Commission } from '../types/commission'

export type ScopeType = 'company' | 'division' | 'team'
export type DashboardType = 'volume' | 'units' | 'speed'
export type TimePeriod = 'this_month' | 'last_month' | 'last_3_months' | 'ytd'

export interface LeaderboardEntry {
  rank: number
  name: string
  badge: 'Gold' | 'Platinum' | 'Diamond'
  value: number
  trend?: 'up' | 'down' | 'same'
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
}

export function getInitials(name: string): string {
  return initials(name)
}

// Volume data — values in dollars
const volumeThisMonth: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 1850000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 1620000,
    trend: 'same',
  },
  {
    rank: 3,
    name: 'Katrina Bullock',
    badge: 'Gold',
    value: 1040000,
    trend: 'up',
  },
  {
    rank: 4,
    name: 'Bianca Payton',
    badge: 'Diamond',
    value: 980000,
    trend: 'down',
  },
  {
    rank: 5,
    name: 'Daniel McCarthy',
    badge: 'Gold',
    value: 960000,
    trend: 'up',
  },
  {
    rank: 6,
    name: 'Lucas Rohr',
    badge: 'Platinum',
    value: 770000,
    trend: 'same',
  },
  { rank: 7, name: 'Charles Ryle', badge: 'Gold', value: 720000, trend: 'up' },
  {
    rank: 8,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 600000,
    trend: 'down',
  },
  { rank: 9, name: 'Antonio Hayes', badge: 'Gold', value: 480000, trend: 'up' },
  {
    rank: 10,
    name: 'Mike Kounini',
    badge: 'Gold',
    value: 350000,
    trend: 'same',
  },
  {
    rank: 11,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 310000,
    trend: 'down',
  },
  {
    rank: 12,
    name: 'Derek Walton',
    badge: 'Gold',
    value: 280000,
    trend: 'same',
  },
]

const volumeLastMonth: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 2100000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 1740000,
    trend: 'down',
  },
  {
    rank: 3,
    name: 'Bianca Payton',
    badge: 'Diamond',
    value: 1380000,
    trend: 'up',
  },
  {
    rank: 4,
    name: 'Lucas Rohr',
    badge: 'Platinum',
    value: 1100000,
    trend: 'up',
  },
  {
    rank: 5,
    name: 'Katrina Bullock',
    badge: 'Gold',
    value: 920000,
    trend: 'down',
  },
  {
    rank: 6,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 850000,
    trend: 'up',
  },
  {
    rank: 7,
    name: 'Daniel McCarthy',
    badge: 'Gold',
    value: 780000,
    trend: 'same',
  },
  {
    rank: 8,
    name: 'Charles Ryle',
    badge: 'Gold',
    value: 650000,
    trend: 'down',
  },
  {
    rank: 9,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 520000,
    trend: 'up',
  },
  {
    rank: 10,
    name: 'Antonio Hayes',
    badge: 'Gold',
    value: 410000,
    trend: 'same',
  },
  {
    rank: 11,
    name: 'Derek Walton',
    badge: 'Gold',
    value: 320000,
    trend: 'down',
  },
  { rank: 12, name: 'Mike Kounini', badge: 'Gold', value: 290000, trend: 'up' },
]

const volumeLast3Months: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 5400000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 4900000,
    trend: 'same',
  },
  {
    rank: 3,
    name: 'Bianca Payton',
    badge: 'Diamond',
    value: 3800000,
    trend: 'up',
  },
  {
    rank: 4,
    name: 'Lucas Rohr',
    badge: 'Platinum',
    value: 2900000,
    trend: 'up',
  },
  {
    rank: 5,
    name: 'Katrina Bullock',
    badge: 'Gold',
    value: 2700000,
    trend: 'down',
  },
  {
    rank: 6,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 2200000,
    trend: 'same',
  },
  {
    rank: 7,
    name: 'Daniel McCarthy',
    badge: 'Gold',
    value: 2100000,
    trend: 'up',
  },
  {
    rank: 8,
    name: 'Charles Ryle',
    badge: 'Gold',
    value: 1800000,
    trend: 'down',
  },
  {
    rank: 9,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 1400000,
    trend: 'up',
  },
  {
    rank: 10,
    name: 'Antonio Hayes',
    badge: 'Gold',
    value: 1100000,
    trend: 'same',
  },
  {
    rank: 11,
    name: 'Mike Kounini',
    badge: 'Gold',
    value: 850000,
    trend: 'down',
  },
  {
    rank: 12,
    name: 'Derek Walton',
    badge: 'Gold',
    value: 720000,
    trend: 'same',
  },
]

const volumeYtd: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 8200000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 7500000,
    trend: 'same',
  },
  {
    rank: 3,
    name: 'Bianca Payton',
    badge: 'Diamond',
    value: 6100000,
    trend: 'up',
  },
  {
    rank: 4,
    name: 'Lucas Rohr',
    badge: 'Platinum',
    value: 4800000,
    trend: 'up',
  },
  {
    rank: 5,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 4200000,
    trend: 'same',
  },
  {
    rank: 6,
    name: 'Katrina Bullock',
    badge: 'Gold',
    value: 3900000,
    trend: 'down',
  },
  {
    rank: 7,
    name: 'Daniel McCarthy',
    badge: 'Gold',
    value: 3400000,
    trend: 'up',
  },
  {
    rank: 8,
    name: 'Charles Ryle',
    badge: 'Gold',
    value: 2800000,
    trend: 'down',
  },
  {
    rank: 9,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 2200000,
    trend: 'up',
  },
  {
    rank: 10,
    name: 'Antonio Hayes',
    badge: 'Gold',
    value: 1700000,
    trend: 'same',
  },
  {
    rank: 11,
    name: 'Mike Kounini',
    badge: 'Gold',
    value: 1300000,
    trend: 'down',
  },
  {
    rank: 12,
    name: 'Derek Walton',
    badge: 'Gold',
    value: 1050000,
    trend: 'same',
  },
]

// Units data — number of loans closed
const unitsThisMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Melanie Rayis', badge: 'Platinum', value: 14, trend: 'up' },
  { rank: 2, name: 'Daniel McCarthy', badge: 'Gold', value: 11, trend: 'up' },
  {
    rank: 3,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 9,
    trend: 'same',
  },
  { rank: 4, name: 'Katrina Bullock', badge: 'Gold', value: 8, trend: 'up' },
  { rank: 5, name: 'Charles Ryle', badge: 'Gold', value: 7, trend: 'down' },
  { rank: 6, name: 'Bianca Payton', badge: 'Diamond', value: 6, trend: 'same' },
  { rank: 7, name: 'Lucas Rohr', badge: 'Platinum', value: 5, trend: 'up' },
  {
    rank: 8,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 5,
    trend: 'down',
  },
  {
    rank: 9,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 4,
    trend: 'same',
  },
  { rank: 10, name: 'Antonio Hayes', badge: 'Gold', value: 3, trend: 'up' },
  { rank: 11, name: 'Derek Walton', badge: 'Gold', value: 2, trend: 'down' },
  { rank: 12, name: 'Mike Kounini', badge: 'Gold', value: 2, trend: 'same' },
]

const unitsLastMonth: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 12,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 11,
    trend: 'down',
  },
  { rank: 3, name: 'Katrina Bullock', badge: 'Gold', value: 9, trend: 'up' },
  { rank: 4, name: 'Bianca Payton', badge: 'Diamond', value: 8, trend: 'same' },
  { rank: 5, name: 'Daniel McCarthy', badge: 'Gold', value: 7, trend: 'down' },
  { rank: 6, name: 'Lucas Rohr', badge: 'Platinum', value: 6, trend: 'up' },
  {
    rank: 7,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 6,
    trend: 'same',
  },
  { rank: 8, name: 'Charles Ryle', badge: 'Gold', value: 5, trend: 'up' },
  { rank: 9, name: 'Antonio Hayes', badge: 'Gold', value: 4, trend: 'same' },
  {
    rank: 10,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 3,
    trend: 'down',
  },
  { rank: 11, name: 'Mike Kounini', badge: 'Gold', value: 2, trend: 'up' },
  { rank: 12, name: 'Derek Walton', badge: 'Gold', value: 2, trend: 'same' },
]

const unitsLast3Months: LeaderboardEntry[] = [
  { rank: 1, name: 'Melanie Rayis', badge: 'Platinum', value: 38, trend: 'up' },
  {
    rank: 2,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 32,
    trend: 'same',
  },
  { rank: 3, name: 'Daniel McCarthy', badge: 'Gold', value: 27, trend: 'up' },
  { rank: 4, name: 'Katrina Bullock', badge: 'Gold', value: 24, trend: 'down' },
  { rank: 5, name: 'Bianca Payton', badge: 'Diamond', value: 21, trend: 'up' },
  { rank: 6, name: 'Charles Ryle', badge: 'Gold', value: 18, trend: 'same' },
  { rank: 7, name: 'Lucas Rohr', badge: 'Platinum', value: 16, trend: 'up' },
  {
    rank: 8,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 14,
    trend: 'down',
  },
  {
    rank: 9,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 11,
    trend: 'up',
  },
  { rank: 10, name: 'Antonio Hayes', badge: 'Gold', value: 9, trend: 'same' },
  { rank: 11, name: 'Mike Kounini', badge: 'Gold', value: 7, trend: 'down' },
  { rank: 12, name: 'Derek Walton', badge: 'Gold', value: 5, trend: 'same' },
]

const unitsYtd: LeaderboardEntry[] = [
  { rank: 1, name: 'Melanie Rayis', badge: 'Platinum', value: 52, trend: 'up' },
  {
    rank: 2,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 45,
    trend: 'same',
  },
  { rank: 3, name: 'Daniel McCarthy', badge: 'Gold', value: 38, trend: 'up' },
  { rank: 4, name: 'Bianca Payton', badge: 'Diamond', value: 34, trend: 'up' },
  { rank: 5, name: 'Katrina Bullock', badge: 'Gold', value: 31, trend: 'down' },
  { rank: 6, name: 'Lucas Rohr', badge: 'Platinum', value: 28, trend: 'same' },
  { rank: 7, name: 'Charles Ryle', badge: 'Gold', value: 25, trend: 'up' },
  {
    rank: 8,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 22,
    trend: 'down',
  },
  {
    rank: 9,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 18,
    trend: 'up',
  },
  { rank: 10, name: 'Antonio Hayes', badge: 'Gold', value: 14, trend: 'same' },
  { rank: 11, name: 'Mike Kounini', badge: 'Gold', value: 10, trend: 'down' },
  { rank: 12, name: 'Derek Walton', badge: 'Gold', value: 8, trend: 'same' },
]

// Speed to Close data — average days (lower is better)
const speedThisMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Bianca Payton', badge: 'Diamond', value: 16, trend: 'up' },
  { rank: 2, name: 'Lucas Rohr', badge: 'Platinum', value: 18, trend: 'same' },
  {
    rank: 3,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 19,
    trend: 'up',
  },
  {
    rank: 4,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 21,
    trend: 'down',
  },
  {
    rank: 5,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 22,
    trend: 'same',
  },
  { rank: 6, name: 'Katrina Bullock', badge: 'Gold', value: 24, trend: 'up' },
  { rank: 7, name: 'Daniel McCarthy', badge: 'Gold', value: 25, trend: 'down' },
  { rank: 8, name: 'Charles Ryle', badge: 'Gold', value: 27, trend: 'same' },
  {
    rank: 9,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 28,
    trend: 'up',
  },
  { rank: 10, name: 'Antonio Hayes', badge: 'Gold', value: 30, trend: 'down' },
  { rank: 11, name: 'Mike Kounini', badge: 'Gold', value: 32, trend: 'same' },
  { rank: 12, name: 'Derek Walton', badge: 'Gold', value: 35, trend: 'down' },
]

const speedLastMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Lucas Rohr', badge: 'Platinum', value: 15, trend: 'up' },
  {
    rank: 2,
    name: 'Bianca Payton',
    badge: 'Diamond',
    value: 17,
    trend: 'down',
  },
  {
    rank: 3,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 19,
    trend: 'up',
  },
  {
    rank: 4,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 20,
    trend: 'same',
  },
  { rank: 5, name: 'Katrina Bullock', badge: 'Gold', value: 22, trend: 'up' },
  {
    rank: 6,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 23,
    trend: 'down',
  },
  { rank: 7, name: 'Charles Ryle', badge: 'Gold', value: 25, trend: 'same' },
  { rank: 8, name: 'Daniel McCarthy', badge: 'Gold', value: 26, trend: 'up' },
  { rank: 9, name: 'Antonio Hayes', badge: 'Gold', value: 29, trend: 'down' },
  {
    rank: 10,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 30,
    trend: 'same',
  },
  { rank: 11, name: 'Derek Walton', badge: 'Gold', value: 33, trend: 'up' },
  { rank: 12, name: 'Mike Kounini', badge: 'Gold', value: 36, trend: 'down' },
]

const speedLast3Months: LeaderboardEntry[] = [
  { rank: 1, name: 'Bianca Payton', badge: 'Diamond', value: 17, trend: 'up' },
  { rank: 2, name: 'Lucas Rohr', badge: 'Platinum', value: 18, trend: 'same' },
  {
    rank: 3,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 19,
    trend: 'up',
  },
  {
    rank: 4,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 21,
    trend: 'same',
  },
  {
    rank: 5,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 22,
    trend: 'down',
  },
  { rank: 6, name: 'Katrina Bullock', badge: 'Gold', value: 24, trend: 'up' },
  { rank: 7, name: 'Daniel McCarthy', badge: 'Gold', value: 25, trend: 'same' },
  { rank: 8, name: 'Charles Ryle', badge: 'Gold', value: 27, trend: 'down' },
  { rank: 9, name: 'Antonio Hayes', badge: 'Gold', value: 29, trend: 'up' },
  {
    rank: 10,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 30,
    trend: 'same',
  },
  { rank: 11, name: 'Mike Kounini', badge: 'Gold', value: 33, trend: 'down' },
  { rank: 12, name: 'Derek Walton', badge: 'Gold', value: 35, trend: 'same' },
]

const speedYtd: LeaderboardEntry[] = [
  { rank: 1, name: 'Bianca Payton', badge: 'Diamond', value: 18, trend: 'up' },
  { rank: 2, name: 'Lucas Rohr', badge: 'Platinum', value: 19, trend: 'same' },
  {
    rank: 3,
    name: 'LaMonte Stinnette',
    badge: 'Diamond',
    value: 20,
    trend: 'up',
  },
  {
    rank: 4,
    name: 'Zaid Shuwayhat',
    badge: 'Platinum',
    value: 21,
    trend: 'same',
  },
  {
    rank: 5,
    name: 'Melanie Rayis',
    badge: 'Platinum',
    value: 23,
    trend: 'down',
  },
  { rank: 6, name: 'Katrina Bullock', badge: 'Gold', value: 24, trend: 'up' },
  { rank: 7, name: 'Daniel McCarthy', badge: 'Gold', value: 26, trend: 'same' },
  { rank: 8, name: 'Charles Ryle', badge: 'Gold', value: 27, trend: 'down' },
  {
    rank: 9,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 29,
    trend: 'up',
  },
  { rank: 10, name: 'Antonio Hayes', badge: 'Gold', value: 31, trend: 'same' },
  { rank: 11, name: 'Mike Kounini', badge: 'Gold', value: 34, trend: 'down' },
  { rank: 12, name: 'Derek Walton', badge: 'Gold', value: 36, trend: 'same' },
]

// === DIVISION-LEVEL RANKINGS ===
// These rank actual divisions as entities, not individual producers

export const CURRENT_USER_DIVISION = 'Northeast'

const divRankVolumeThisMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Northeast', badge: 'Diamond', value: 8200000, trend: 'up' },
  {
    rank: 2,
    name: 'West Coast',
    badge: 'Platinum',
    value: 7500000,
    trend: 'same',
  },
  {
    rank: 3,
    name: 'Southeast',
    badge: 'Platinum',
    value: 6800000,
    trend: 'up',
  },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 5400000, trend: 'down' },
  { rank: 5, name: 'Southwest', badge: 'Gold', value: 4100000, trend: 'up' },
  { rank: 6, name: 'Pacific NW', badge: 'Gold', value: 3200000, trend: 'same' },
]

const divRankVolumeLastMonth: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'West Coast',
    badge: 'Diamond',
    value: 8000000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Northeast',
    badge: 'Platinum',
    value: 7800000,
    trend: 'down',
  },
  {
    rank: 3,
    name: 'Southeast',
    badge: 'Platinum',
    value: 6500000,
    trend: 'same',
  },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 5800000, trend: 'up' },
  { rank: 5, name: 'Southwest', badge: 'Gold', value: 3900000, trend: 'down' },
  { rank: 6, name: 'Pacific NW', badge: 'Gold', value: 3000000, trend: 'same' },
]

const divRankVolumeLast3Months: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'Northeast',
    badge: 'Diamond',
    value: 22500000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'West Coast',
    badge: 'Platinum',
    value: 21000000,
    trend: 'same',
  },
  {
    rank: 3,
    name: 'Southeast',
    badge: 'Platinum',
    value: 18500000,
    trend: 'up',
  },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 15200000, trend: 'down' },
  { rank: 5, name: 'Southwest', badge: 'Gold', value: 11800000, trend: 'up' },
  { rank: 6, name: 'Pacific NW', badge: 'Gold', value: 9500000, trend: 'same' },
]

const divRankVolumeYtd: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'West Coast',
    badge: 'Diamond',
    value: 45000000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Northeast',
    badge: 'Diamond',
    value: 43500000,
    trend: 'same',
  },
  {
    rank: 3,
    name: 'Southeast',
    badge: 'Platinum',
    value: 38000000,
    trend: 'up',
  },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 31000000, trend: 'down' },
  { rank: 5, name: 'Southwest', badge: 'Gold', value: 24500000, trend: 'up' },
  {
    rank: 6,
    name: 'Pacific NW',
    badge: 'Gold',
    value: 19000000,
    trend: 'same',
  },
]

const divRankUnitsThisMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Northeast', badge: 'Diamond', value: 32, trend: 'up' },
  { rank: 2, name: 'Southeast', badge: 'Platinum', value: 28, trend: 'same' },
  { rank: 3, name: 'West Coast', badge: 'Platinum', value: 26, trend: 'down' },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 22, trend: 'up' },
  { rank: 5, name: 'Southwest', badge: 'Gold', value: 18, trend: 'same' },
  { rank: 6, name: 'Pacific NW', badge: 'Gold', value: 14, trend: 'down' },
]

const divRankUnitsLastMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Southeast', badge: 'Diamond', value: 30, trend: 'up' },
  { rank: 2, name: 'Northeast', badge: 'Platinum', value: 29, trend: 'same' },
  { rank: 3, name: 'West Coast', badge: 'Platinum', value: 27, trend: 'down' },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 24, trend: 'up' },
  { rank: 5, name: 'Southwest', badge: 'Gold', value: 16, trend: 'down' },
  { rank: 6, name: 'Pacific NW', badge: 'Gold', value: 12, trend: 'same' },
]

const divRankUnitsLast3Months: LeaderboardEntry[] = [
  { rank: 1, name: 'Northeast', badge: 'Diamond', value: 88, trend: 'up' },
  { rank: 2, name: 'Southeast', badge: 'Platinum', value: 82, trend: 'same' },
  { rank: 3, name: 'West Coast', badge: 'Platinum', value: 78, trend: 'down' },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 65, trend: 'up' },
  { rank: 5, name: 'Southwest', badge: 'Gold', value: 50, trend: 'same' },
  { rank: 6, name: 'Pacific NW', badge: 'Gold', value: 38, trend: 'down' },
]

const divRankUnitsYtd: LeaderboardEntry[] = [
  { rank: 1, name: 'Southeast', badge: 'Diamond', value: 165, trend: 'up' },
  { rank: 2, name: 'Northeast', badge: 'Diamond', value: 160, trend: 'same' },
  { rank: 3, name: 'West Coast', badge: 'Platinum', value: 152, trend: 'down' },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 128, trend: 'up' },
  { rank: 5, name: 'Southwest', badge: 'Gold', value: 98, trend: 'same' },
  { rank: 6, name: 'Pacific NW', badge: 'Gold', value: 75, trend: 'down' },
]

const divRankSpeedThisMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Pacific NW', badge: 'Diamond', value: 18, trend: 'up' },
  { rank: 2, name: 'West Coast', badge: 'Platinum', value: 21, trend: 'same' },
  { rank: 3, name: 'Northeast', badge: 'Platinum', value: 24, trend: 'up' },
  { rank: 4, name: 'Southeast', badge: 'Gold', value: 26, trend: 'down' },
  { rank: 5, name: 'Midwest', badge: 'Gold', value: 29, trend: 'same' },
  { rank: 6, name: 'Southwest', badge: 'Gold', value: 32, trend: 'down' },
]

const divRankSpeedLastMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Pacific NW', badge: 'Diamond', value: 19, trend: 'up' },
  { rank: 2, name: 'West Coast', badge: 'Platinum', value: 22, trend: 'same' },
  { rank: 3, name: 'Northeast', badge: 'Platinum', value: 25, trend: 'down' },
  { rank: 4, name: 'Midwest', badge: 'Gold', value: 27, trend: 'up' },
  { rank: 5, name: 'Southeast', badge: 'Gold', value: 28, trend: 'same' },
  { rank: 6, name: 'Southwest', badge: 'Gold', value: 33, trend: 'down' },
]

const divRankSpeedLast3Months: LeaderboardEntry[] = [
  { rank: 1, name: 'Pacific NW', badge: 'Diamond', value: 19, trend: 'up' },
  { rank: 2, name: 'West Coast', badge: 'Platinum', value: 21, trend: 'same' },
  { rank: 3, name: 'Northeast', badge: 'Platinum', value: 23, trend: 'up' },
  { rank: 4, name: 'Southeast', badge: 'Gold', value: 26, trend: 'down' },
  { rank: 5, name: 'Midwest', badge: 'Gold', value: 28, trend: 'same' },
  { rank: 6, name: 'Southwest', badge: 'Gold', value: 31, trend: 'down' },
]

const divRankSpeedYtd: LeaderboardEntry[] = [
  { rank: 1, name: 'Pacific NW', badge: 'Diamond', value: 20, trend: 'up' },
  { rank: 2, name: 'West Coast', badge: 'Platinum', value: 22, trend: 'same' },
  { rank: 3, name: 'Northeast', badge: 'Platinum', value: 24, trend: 'up' },
  { rank: 4, name: 'Southeast', badge: 'Gold', value: 27, trend: 'down' },
  { rank: 5, name: 'Midwest', badge: 'Gold', value: 29, trend: 'same' },
  { rank: 6, name: 'Southwest', badge: 'Gold', value: 32, trend: 'down' },
]

// === TEAM DATA ===
const teamVolumeThisMonth: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'Katrina Bullock',
    badge: 'Gold',
    value: 1040000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Daniel McCarthy',
    badge: 'Gold',
    value: 960000,
    trend: 'up',
  },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 720000, trend: 'up' },
  {
    rank: 4,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 310000,
    trend: 'down',
  },
  {
    rank: 5,
    name: 'Derek Walton',
    badge: 'Gold',
    value: 280000,
    trend: 'same',
  },
]

const teamVolumeLastMonth: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'Daniel McCarthy',
    badge: 'Gold',
    value: 780000,
    trend: 'same',
  },
  {
    rank: 2,
    name: 'Katrina Bullock',
    badge: 'Gold',
    value: 920000,
    trend: 'down',
  },
  {
    rank: 3,
    name: 'Charles Ryle',
    badge: 'Gold',
    value: 650000,
    trend: 'down',
  },
  {
    rank: 4,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 520000,
    trend: 'up',
  },
  {
    rank: 5,
    name: 'Derek Walton',
    badge: 'Gold',
    value: 320000,
    trend: 'down',
  },
]

const teamVolumeLast3Months: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'Katrina Bullock',
    badge: 'Gold',
    value: 2800000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Daniel McCarthy',
    badge: 'Gold',
    value: 2400000,
    trend: 'same',
  },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 1900000, trend: 'up' },
  {
    rank: 4,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 1100000,
    trend: 'down',
  },
  {
    rank: 5,
    name: 'Derek Walton',
    badge: 'Gold',
    value: 850000,
    trend: 'same',
  },
]

const teamVolumeYtd: LeaderboardEntry[] = [
  {
    rank: 1,
    name: 'Daniel McCarthy',
    badge: 'Gold',
    value: 5200000,
    trend: 'up',
  },
  {
    rank: 2,
    name: 'Katrina Bullock',
    badge: 'Gold',
    value: 4800000,
    trend: 'same',
  },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 3600000, trend: 'up' },
  {
    rank: 4,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 2200000,
    trend: 'down',
  },
  {
    rank: 5,
    name: 'Derek Walton',
    badge: 'Gold',
    value: 1700000,
    trend: 'same',
  },
]

const teamUnitsThisMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Katrina Bullock', badge: 'Gold', value: 5, trend: 'up' },
  { rank: 2, name: 'Daniel McCarthy', badge: 'Gold', value: 4, trend: 'same' },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 3, trend: 'up' },
  {
    rank: 4,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 2,
    trend: 'down',
  },
  { rank: 5, name: 'Derek Walton', badge: 'Gold', value: 1, trend: 'same' },
]

const teamUnitsLastMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Daniel McCarthy', badge: 'Gold', value: 5, trend: 'up' },
  { rank: 2, name: 'Katrina Bullock', badge: 'Gold', value: 4, trend: 'down' },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 3, trend: 'same' },
  { rank: 4, name: 'Jasmine Torres', badge: 'Platinum', value: 3, trend: 'up' },
  { rank: 5, name: 'Derek Walton', badge: 'Gold', value: 2, trend: 'down' },
]

const teamUnitsLast3Months: LeaderboardEntry[] = [
  { rank: 1, name: 'Katrina Bullock', badge: 'Gold', value: 14, trend: 'up' },
  { rank: 2, name: 'Daniel McCarthy', badge: 'Gold', value: 12, trend: 'same' },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 9, trend: 'up' },
  {
    rank: 4,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 7,
    trend: 'down',
  },
  { rank: 5, name: 'Derek Walton', badge: 'Gold', value: 5, trend: 'same' },
]

const teamUnitsYtd: LeaderboardEntry[] = [
  { rank: 1, name: 'Daniel McCarthy', badge: 'Gold', value: 26, trend: 'up' },
  { rank: 2, name: 'Katrina Bullock', badge: 'Gold', value: 24, trend: 'same' },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 18, trend: 'up' },
  {
    rank: 4,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 14,
    trend: 'down',
  },
  { rank: 5, name: 'Derek Walton', badge: 'Gold', value: 10, trend: 'same' },
]

const teamSpeedThisMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Derek Walton', badge: 'Gold', value: 18, trend: 'up' },
  { rank: 2, name: 'Katrina Bullock', badge: 'Gold', value: 22, trend: 'up' },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 25, trend: 'same' },
  { rank: 4, name: 'Daniel McCarthy', badge: 'Gold', value: 28, trend: 'down' },
  {
    rank: 5,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 32,
    trend: 'down',
  },
]

const teamSpeedLastMonth: LeaderboardEntry[] = [
  { rank: 1, name: 'Derek Walton', badge: 'Gold', value: 19, trend: 'up' },
  { rank: 2, name: 'Charles Ryle', badge: 'Gold', value: 24, trend: 'same' },
  { rank: 3, name: 'Katrina Bullock', badge: 'Gold', value: 25, trend: 'down' },
  { rank: 4, name: 'Daniel McCarthy', badge: 'Gold', value: 27, trend: 'up' },
  {
    rank: 5,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 30,
    trend: 'same',
  },
]

const teamSpeedLast3Months: LeaderboardEntry[] = [
  { rank: 1, name: 'Derek Walton', badge: 'Gold', value: 19, trend: 'up' },
  { rank: 2, name: 'Katrina Bullock', badge: 'Gold', value: 23, trend: 'up' },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 24, trend: 'same' },
  { rank: 4, name: 'Daniel McCarthy', badge: 'Gold', value: 27, trend: 'down' },
  {
    rank: 5,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 31,
    trend: 'down',
  },
]

const teamSpeedYtd: LeaderboardEntry[] = [
  { rank: 1, name: 'Derek Walton', badge: 'Gold', value: 20, trend: 'up' },
  { rank: 2, name: 'Katrina Bullock', badge: 'Gold', value: 23, trend: 'same' },
  { rank: 3, name: 'Charles Ryle', badge: 'Gold', value: 25, trend: 'up' },
  { rank: 4, name: 'Daniel McCarthy', badge: 'Gold', value: 28, trend: 'down' },
  {
    rank: 5,
    name: 'Jasmine Torres',
    badge: 'Platinum',
    value: 31,
    trend: 'same',
  },
]

const LEADERBOARD_DATA: Record<
  ScopeType,
  Record<DashboardType, Record<TimePeriod, LeaderboardEntry[]>>
> = {
  company: {
    volume: {
      this_month: volumeThisMonth,
      last_month: volumeLastMonth,
      last_3_months: volumeLast3Months,
      ytd: volumeYtd,
    },
    units: {
      this_month: unitsThisMonth,
      last_month: unitsLastMonth,
      last_3_months: unitsLast3Months,
      ytd: unitsYtd,
    },
    speed: {
      this_month: speedThisMonth,
      last_month: speedLastMonth,
      last_3_months: speedLast3Months,
      ytd: speedYtd,
    },
  },
  division: {
    volume: {
      this_month: divRankVolumeThisMonth,
      last_month: divRankVolumeLastMonth,
      last_3_months: divRankVolumeLast3Months,
      ytd: divRankVolumeYtd,
    },
    units: {
      this_month: divRankUnitsThisMonth,
      last_month: divRankUnitsLastMonth,
      last_3_months: divRankUnitsLast3Months,
      ytd: divRankUnitsYtd,
    },
    speed: {
      this_month: divRankSpeedThisMonth,
      last_month: divRankSpeedLastMonth,
      last_3_months: divRankSpeedLast3Months,
      ytd: divRankSpeedYtd,
    },
  },
  team: {
    volume: {
      this_month: teamVolumeThisMonth,
      last_month: teamVolumeLastMonth,
      last_3_months: teamVolumeLast3Months,
      ytd: teamVolumeYtd,
    },
    units: {
      this_month: teamUnitsThisMonth,
      last_month: teamUnitsLastMonth,
      last_3_months: teamUnitsLast3Months,
      ytd: teamUnitsYtd,
    },
    speed: {
      this_month: teamSpeedThisMonth,
      last_month: teamSpeedLastMonth,
      last_3_months: teamSpeedLast3Months,
      ytd: teamSpeedYtd,
    },
  },
}

export function getLeaderboardData(
  scope: ScopeType,
  type: DashboardType,
  period: TimePeriod,
): LeaderboardEntry[] {
  return LEADERBOARD_DATA[scope][type][period]
}

export interface EnrichedLeaderboardEntry extends LeaderboardEntry {
  previousValue: number | null
  previousRank: number | null
  region: string
  avatarColor: string
}

function getStringHash(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash)
}

const REGIONS = [
  'Northeast',
  'Southeast',
  'West Coast',
  'Midwest',
  'Southwest',
  'Pacific NW',
]

const AVATAR_COLORS = [
  'bg-cyan-600',
  'bg-violet-600',
  'bg-rose-600',
  'bg-amber-600',
  'bg-emerald-600',
  'bg-blue-600',
  'bg-pink-600',
  'bg-teal-600',
]

export function getEnrichedLeaderboardData(
  scope: ScopeType,
  type: DashboardType,
  period: TimePeriod,
): EnrichedLeaderboardEntry[] {
  const currentData = getLeaderboardData(scope, type, period)

  let previousPeriod: TimePeriod | null = null
  if (period === 'this_month') previousPeriod = 'last_month'
  else if (period === 'last_month') previousPeriod = 'last_3_months'
  else if (period === 'last_3_months') previousPeriod = 'ytd'

  const previousData = previousPeriod
    ? getLeaderboardData(scope, type, previousPeriod)
    : []

  return currentData.map((entry) => {
    const hash = getStringHash(entry.name)
    const region = REGIONS[hash % REGIONS.length]
    const avatarColor = AVATAR_COLORS[hash % AVATAR_COLORS.length]

    const prevEntry = previousData.find((p) => p.name === entry.name)

    return {
      ...entry,
      previousValue: prevEntry ? prevEntry.value : null,
      previousRank: prevEntry ? prevEntry.rank : null,
      region,
      avatarColor,
    }
  })
}
